import { Flex, Text } from "@hackthenorth/north";
import moment from "moment";
import { Link, Table } from "north.js";
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

import {
  errorToast,
  Icon,
  PageBar,
  PageWrapper,
  Spinner,
  successToast,
  TooltipWrapper,
} from "src/shared/components";
import Header from "src/shared/components/Header";
import { EVENT_ID } from "src/shared/constants/event";
import { VolunteerRoute } from "src/shared/constants/route";
import { FileDownloadIcon } from "src/static/icons";
import ScheduleToggle from "src/views/schedule/components/ScheduleToggle";
import { SCHEDULE_VIEW_TYPE } from "src/views/schedule/constants";

import { ShiftSearchBar } from "../../shared/components/ShiftSearchBar";
import VolunteerShiftModal from "../../shared/components/VolunteerShiftModal";
import {
  getBufferRemovedFromNeighbors,
  getBufferedShift,
  projectWithCandidate,
  TBufferShift,
} from "../../shared/utils/buffer";
import { downloadVolunteerShiftsIcs } from "../../shared/utils/ics";
import { ScheduleFilterBar } from "../components/ScheduleFilterBar";
import { ShiftLegend } from "../components/ShiftLegend";
import SignupConfirmModal, {
  TSignupConfirmShift,
} from "../components/SignupConfirmModal";
import {
  DEFAULT_MIN_SHIFTS,
  generateDisableReasonToolTip,
  PRIMARY_ROLES_ONLY,
} from "../constants";
import { useDeleteShiftSignupSelfMutation } from "../graphql/deleteShiftSignupSelf.generated";
import { useGetMyVolunteerShiftSignupsQuery } from "../graphql/getMyVolunteerShiftSignups.generated";
import { useGetVolunteerSelfQuery } from "../graphql/getVolunteerSelf.generated";
import { useGetVolunteerShiftAvailabilityQuery } from "../graphql/getVolunteerShiftAvailability.generated";
import { useGetVolunteerShiftScheduleQuery } from "../graphql/getVolunteerShiftSchedule.generated";
import { useSignupForShiftMutation } from "../graphql/signupForShift.generated";
import {
  DISABLE_REASONS,
  TIsDisabled,
  TIsRegistered,
  TShiftSchedule,
} from "../types";
import {
  isOvernightShift,
  parseAdminShifts,
  shiftMatchesSearch,
} from "../utils";

import VolunteerShiftCalendarView from "./VolunteerShiftCalendarView";

export const VolunteerShiftSchedule = () => {
  // const [overlayOpen, setOverlayOpen] = useState<boolean>(false);

  const [shiftFilters, setShiftFilters] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [onlyMyShifts, setOnlyMyShifts] = useState<boolean>(false);
  const [onlyAvailableShifts, setOnlyAvailableShifts] =
    useState<boolean>(false);

  const [selectedShift, setSelectedShift] = React.useState<number | null>(null);
  const [viewModal, setViewModal] = React.useState<boolean>(false);
  const [pendingSignup, setPendingSignup] = useState<{
    shiftId: number;
    index: number;
    name: string;
  } | null>(null);

  const [currentView, setCurrentView] = useState<SCHEDULE_VIEW_TYPE>(
    SCHEDULE_VIEW_TYPE.CALENDAR_VIEW
  );

  const [page, setPage] = useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  const [primaryRole, setPrimaryRole] = useState("");
  const [isRegistered, setIsRegistered] = useState<TIsRegistered[]>([]);
  const [additionalRolesCount, setAdditionalRolesCount] = useState(0);
  const [primaryRolesCount, setPrimaryRolesCount] = useState(0);
  const [overNightShiftCount, setOverNightShiftCount] = useState(0);
  const [minSignUps, setMinSignUps] = useState(DEFAULT_MIN_SHIFTS);
  const [hasReadHandbook, setHasReadHandbook] = useState(false);

  const {
    data: getVolunteerShiftsData,
    loading: fetchingShifts,
    refetch: refetchAllShifts,
  } = useGetVolunteerShiftScheduleQuery({
    variables: { event_id: EVENT_ID },
  });
  const {
    data: getVolunteerShiftAvailabilityData,
    loading: fetchingVolunteerShiftAvailability,
    refetch: refetchVolunteerShiftAvailability,
  } = useGetVolunteerShiftAvailabilityQuery({
    variables: { event_id: EVENT_ID },
    // Polling remains paused; reload or use action-based refetches.
  });
  const {
    data: getMyVolunteerShiftSignups,
    loading: fetchingMyVolunteerShiftSignups,
    refetch: refetchMyVolunteerShiftSignups,
  } = useGetMyVolunteerShiftSignupsQuery({ variables: { event_id: EVENT_ID } });
  const { data: getVolunteerSelf, loading: fetchingVolunteerSelf } =
    useGetVolunteerSelfQuery({
      variables: { event_id: EVENT_ID },
    });
  const [signupForShift, signupForShiftResult] = useSignupForShiftMutation();
  const [deleteShiftSignup, deleteShiftSignupResult] =
    useDeleteShiftSignupSelfMutation();

  const [adminShifts, setAdminShifts] = useState<TShiftSchedule[]>([]);

  // a refetch can land between an optimistic flip and its mutation, so a shift
  // with a mutation in flight keeps its pending value over the server's
  const pendingSignups = useRef(new Map<number, boolean>());
  const resolveRegistered = (shiftId: number, fromServer: boolean) =>
    pendingSignups.current.get(shiftId) ?? fromServer;

  const handleShiftClick = (shift: TShiftSchedule) => {
    setSelectedShift(shift.shift_id);
    setViewModal(true);
  };

  const toggleView = () => {
    setCurrentView((currentView) => {
      if (currentView === SCHEDULE_VIEW_TYPE.LIST_VIEW)
        return SCHEDULE_VIEW_TYPE.CALENDAR_VIEW;

      return SCHEDULE_VIEW_TYPE.LIST_VIEW;
    });
  };

  useEffect(() => {
    if (viewModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [viewModal]);

  // REFETCH MY VOLUNTEER SHIFTS AFTER DELETING A SHIFT
  useEffect(() => {
    if (deleteShiftSignupResult.data) {
      refetchMyVolunteerShiftSignups();
    }
  }, [deleteShiftSignupResult.data, refetchMyVolunteerShiftSignups]);

  // REFETCH MY VOLUNTEER SHIFTS AFTER SIGNING UP FOR A SHIFT
  useEffect(() => {
    if (signupForShiftResult.data) {
      refetchMyVolunteerShiftSignups();
    }
  }, [signupForShiftResult.data, refetchMyVolunteerShiftSignups]);

  useEffect(() => {
    if (
      getVolunteerShiftsData &&
      !fetchingShifts &&
      getVolunteerShiftAvailabilityData &&
      !fetchingVolunteerShiftAvailability
    ) {
      const newShifts = parseAdminShifts(
        getVolunteerShiftsData,
        getVolunteerShiftAvailabilityData
      );

      setAdminShifts(newShifts);
    }
  }, [
    getVolunteerShiftsData,
    fetchingShifts,
    getVolunteerShiftAvailabilityData,
    fetchingVolunteerShiftAvailability,
  ]);

  useEffect(() => {
    if (
      getMyVolunteerShiftSignups &&
      !fetchingMyVolunteerShiftSignups &&
      getVolunteerShiftsData &&
      !fetchingShifts
    ) {
      let primaryCount = 0;
      let additionalCount = 0;
      let overNightCount = 0;

      getMyVolunteerShiftSignups.myVolunteerShiftSignups.forEach(
        (myVolunteerShiftSignup) => {
          getVolunteerShiftsData.volunteerShiftSchedule.forEach(
            (volunteerShift) => {
              if (myVolunteerShiftSignup.shift_id === volunteerShift.shift_id) {
                if (
                  volunteerShift.category_name ===
                    getVolunteerSelf?.volunteerSelf.primary_category_name &&
                  primaryCount < minSignUps
                ) {
                  primaryCount += 1;
                } else if (
                  !PRIMARY_ROLES_ONLY.includes(volunteerShift.category_name)
                ) {
                  additionalCount += 1;
                }

                const momentStart = moment(volunteerShift.start_time).tz(
                  "America/New_York"
                );
                const momentEnd = moment(volunteerShift.end_time).tz(
                  "America/New_York"
                );

                if (isOvernightShift(momentStart, momentEnd))
                  overNightCount += 1;
              }
            }
          );
        }
      );

      setAdditionalRolesCount(additionalCount);
      setPrimaryRolesCount(primaryCount);
      setOverNightShiftCount(overNightCount);
    }
  }, [
    getVolunteerSelf,
    fetchingMyVolunteerShiftSignups,
    fetchingShifts,
    getMyVolunteerShiftSignups,
    getVolunteerShiftsData,
    minSignUps,
  ]);

  useEffect(() => {
    if (getVolunteerSelf && !fetchingVolunteerSelf) {
      setMinSignUps(getVolunteerSelf.volunteerSelf.min_primary_signups);
      setPrimaryRole(getVolunteerSelf.volunteerSelf.primary_category_name);
      setHasReadHandbook(getVolunteerSelf.volunteerSelf.read_handbook);
    }
  }, [fetchingVolunteerSelf, getVolunteerSelf]);

  useEffect(() => {
    const registeredShiftIds =
      getMyVolunteerShiftSignups?.myVolunteerShiftSignups.map(
        (signup) => signup.shift_id
      ) || [];

    const initialIsRegistered = adminShifts.map((shift) => {
      return {
        registered: resolveRegistered(
          shift.shift_id,
          registeredShiftIds.includes(shift.shift_id)
        ),
        shouldShow: true,
        shift_id: shift.shift_id,
      };
    });

    setIsRegistered(initialIsRegistered);
  }, [getMyVolunteerShiftSignups, adminShifts]);

  // derived (not effect+state) so filter clicks never paint a frame where
  // adminShifts and lock flags are out of sync — that was the gray↔ungray flash.
  // adminShifts stays the full list so time conflicts still see registrations
  // in categories that aren't in the active filter.
  const isDisableRegistered: TIsDisabled[] = useMemo(() => {
    const registeredShiftIds = isRegistered
      .filter((item) => item.registered)
      .map((item) => item.shift_id);

    const registeredShifts = adminShifts.filter((shift) =>
      registeredShiftIds.includes(shift.shift_id)
    );

    return adminShifts.map((shift) => {
      let reason = "";
      let kind: DISABLE_REASONS | undefined;

      const primaryRolesOnlyCondition =
        getVolunteerSelf?.volunteerSelf.primary_category_name !== shift.role &&
        PRIMARY_ROLES_ONLY.includes(shift.role);

      if (primaryRolesOnlyCondition) {
        kind = DISABLE_REASONS.NOT_MATCHING_PRIMARY_ROLE;
        reason = generateDisableReasonToolTip(
          kind,
          getVolunteerSelf?.volunteerSelf.primary_category_name
        );
      }

      const registrationClosed = !shift.registration_open;
      if (registrationClosed && kind === undefined) {
        kind = DISABLE_REASONS.REGISTRATION_CLOSED;
        reason = generateDisableReasonToolTip(kind);
      }

      const hasNoSpotsLeft = shift.spots_left === 0;
      if (hasNoSpotsLeft && kind === undefined) {
        kind = DISABLE_REASONS.NO_SPOTS_LEFT;
        reason = generateDisableReasonToolTip(kind);
      }

      const hasTimeConflict = registeredShifts.filter(
        (registeredShift) =>
          registeredShift.time_start < shift.time_end &&
          registeredShift.time_end > shift.time_start &&
          registeredShift.shift_id !== shift.shift_id
      );

      if (hasTimeConflict.length !== 0 && kind === undefined) {
        kind = DISABLE_REASONS.TIME_CONFLICT;
        reason = generateDisableReasonToolTip(
          kind,
          `${hasTimeConflict[0].shift} at ${
            hasTimeConflict[0].date
          } ${hasTimeConflict[0].time_start.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
            timeZone: "America/New_York",
          })} - ${hasTimeConflict[0].time_end.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
            timeZone: "America/New_York",
          })}`
        );
      }

      const minSignups =
        getVolunteerSelf?.volunteerSelf.primary_category_name !== shift.role &&
        primaryRolesCount < minSignUps;

      if (minSignups && kind === undefined) {
        kind = DISABLE_REASONS.MIN_SIGN_UPS;
        reason = generateDisableReasonToolTip(kind, String(minSignUps));
      }

      const disabledRegistered =
        registrationClosed ||
        hasNoSpotsLeft ||
        hasTimeConflict.length !== 0 ||
        minSignups ||
        primaryRolesOnlyCondition;

      return { disabled: disabledRegistered, reason, kind };
    });
  }, [
    primaryRolesCount,
    minSignUps,
    adminShifts,
    getVolunteerSelf,
    isRegistered,
  ]);

  const setRegisteredAt = (index: number, registered: boolean) =>
    setIsRegistered((prev) =>
      prev.map((item, i) => (i === index ? { ...item, registered } : item))
    );

  const bumpRoleCount = (name: string, delta: number) => {
    const setCount =
      name === getVolunteerSelf?.volunteerSelf.primary_category_name
        ? setPrimaryRolesCount
        : setAdditionalRolesCount;
    setCount((prev) => Math.max(prev + delta, 0));
  };

  // the tile marks, the modal button and the requirement checklist all read off
  // this state, so it flips before the mutation resolves and rolls back if it
  // fails. the refetches update spot counts after the mutation succeeds
  const applySignupChange = async (
    shiftId: number,
    index: number,
    name: string,
    registered: boolean,
    mutate: () => Promise<unknown>,
    successMessage: string
  ) => {
    pendingSignups.current.set(shiftId, registered);
    setRegisteredAt(index, registered);
    bumpRoleCount(name, registered ? 1 : -1);

    try {
      await mutate();
      successToast(successMessage);
      await Promise.all([
        refetchMyVolunteerShiftSignups(),
        refetchAllShifts(),
        refetchVolunteerShiftAvailability(),
      ]);
    } catch (error) {
      setRegisteredAt(index, !registered);
      bumpRoleCount(name, registered ? -1 : 1);
      errorToast("Uh oh! Something went wrong");
      console.error("Error updating shift signup:", error);
    } finally {
      pendingSignups.current.delete(shiftId);
    }
  };

  const handleRegister = (shiftId: number, index: number, name: string) =>
    applySignupChange(
      shiftId,
      index,
      name,
      true,
      () => signupForShift({ variables: { shift_id: shiftId } }),
      "Success! You’ve registered for a shift!"
    );

  const requestRegister = (shiftId: number, index: number, name: string) => {
    setPendingSignup({ shiftId, index, name });
  };

  const handleUnregister = (shiftId: number, index: number, name: string) =>
    applySignupChange(
      shiftId,
      index,
      name,
      false,
      () => deleteShiftSignup({ variables: { shift_id: shiftId } }),
      "Success! You’ve unregistered for a shift!"
    );

  const allShiftsForBuffer = useMemo(() => {
    if (!getVolunteerShiftsData || !getVolunteerShiftAvailabilityData) {
      return adminShifts;
    }
    return parseAdminShifts(
      getVolunteerShiftsData,
      getVolunteerShiftAvailabilityData
    );
  }, [adminShifts, getVolunteerShiftAvailabilityData, getVolunteerShiftsData]);

  const myBufferShifts: TBufferShift[] = useMemo(() => {
    const registeredIds = new Set(
      (getMyVolunteerShiftSignups?.myVolunteerShiftSignups ?? []).map(
        (signup) => signup.shift_id
      )
    );
    return allShiftsForBuffer
      .filter((shift) => registeredIds.has(shift.shift_id))
      .map((shift) => ({
        shiftId: shift.shift_id,
        start: shift.time_start,
        end: shift.time_end,
      }));
  }, [allShiftsForBuffer, getMyVolunteerShiftSignups]);

  const pendingConfirmShift: TSignupConfirmShift | null = useMemo(() => {
    if (!pendingSignup) return null;
    const shift = allShiftsForBuffer.find(
      (s) => s.shift_id === pendingSignup.shiftId
    );
    if (!shift) return null;
    return {
      shiftId: shift.shift_id,
      name: shift.shift,
      start: shift.time_start,
      end: shift.time_end,
    };
  }, [allShiftsForBuffer, pendingSignup]);

  const pendingProjected = useMemo(() => {
    if (!pendingConfirmShift) return undefined;
    return getBufferedShift(
      pendingConfirmShift.shiftId,
      projectWithCandidate(myBufferShifts, {
        shiftId: pendingConfirmShift.shiftId,
        start: pendingConfirmShift.start,
        end: pendingConfirmShift.end,
      })
    );
  }, [myBufferShifts, pendingConfirmShift]);

  const neighborsLosingBuffer = useMemo(() => {
    if (!pendingConfirmShift) return [];
    return getBufferRemovedFromNeighbors(myBufferShifts, {
      shiftId: pendingConfirmShift.shiftId,
      start: pendingConfirmShift.start,
      end: pendingConfirmShift.end,
    });
  }, [myBufferShifts, pendingConfirmShift]);

  const handleExportIcs = () => {
    if (myBufferShifts.length === 0) return;
    const byId = new Map(
      (getVolunteerShiftsData?.volunteerShiftSchedule ?? []).map((shift) => [
        shift.shift_id,
        shift,
      ])
    );
    downloadVolunteerShiftsIcs(
      myBufferShifts.map((shift) => {
        const meta = byId.get(shift.shiftId);
        return {
          shiftId: shift.shiftId,
          start: shift.start,
          end: shift.end,
          name: meta?.name ?? `Shift ${shift.shiftId}`,
          location: meta?.location,
          description: meta?.description,
          shiftLead: meta?.organizer_responsible_name,
          shiftType: meta?.category_name,
        };
      })
    );
  };

  // the lock state and handlers are keyed by position in adminShifts, so the
  // modal needs the same index the table rows use
  const selectedShiftIndex = adminShifts.findIndex(
    (shift) => shift.shift_id === selectedShift
  );

  const runOnSelectedShift = (
    action: (shiftId: number, index: number, role: string) => void
  ) => {
    const shift = adminShifts[selectedShiftIndex];
    if (!shift) return;
    // the signup state flips optimistically, so the calendar behind the modal
    // already shows the result by the time it closes
    setViewModal(false);
    action(shift.shift_id, selectedShiftIndex, shift.role);
  };

  const tableData = adminShifts
    .map((shift: TShiftSchedule, i: number) => ({ shift, i }))
    .filter(
      ({ shift }) =>
        shiftMatchesSearch(shift, search) &&
        (shiftFilters.length === 0 || shiftFilters.includes(shift.role))
    )
    .map(({ shift, i }) => {
      return {
        shift: (
          <div
            onClick={() => handleShiftClick(shift)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleShiftClick(shift);
              }
            }}
            role="button"
            tabIndex={0}
            style={{
              cursor: "pointer",
            }}
          >
            {shift?.shift}
          </div>
        ),
        date: shift?.date,
        time_start: (
          <div style={{ textAlign: "right" }}>
            {shift?.time_start
              .toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "numeric",
                hour12: true,
                timeZone: "America/New_York",
              })
              .replace(":00", "")}
          </div>
        ),
        time_end: (
          <div style={{ textAlign: "right" }}>
            {shift?.time_end
              .toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "numeric",
                hour12: true,
                timeZone: "America/New_York",
              })
              .replace(":00", "")}
          </div>
        ),
        spots_left: (
          <div style={{ textAlign: "center" }}>{shift?.spots_left}</div>
        ),
        roles: <RolesContainer>{shift?.role}</RolesContainer>,
        view: (
          <>
            {isRegistered[i]?.shouldShow ? (
              isRegistered[i]?.registered ? (
                <UnregisterButton
                  onClick={() =>
                    handleUnregister(shift.shift_id, i, shift.role)
                  }
                >
                  Unregister
                </UnregisterButton>
              ) : isDisableRegistered.length > 0 &&
                isDisableRegistered[i] !== undefined &&
                isDisableRegistered[i].disabled ? (
                isDisableRegistered[i].reason === "" ? (
                  <DisabledRegisterButton>Register</DisabledRegisterButton>
                ) : (
                  <TooltipWrapper
                    color={"textSecondary"}
                    icon={
                      <DisabledRegisterButton>Register</DisabledRegisterButton>
                    }
                    content={
                      <TooltipText>{isDisableRegistered[i].reason}</TooltipText>
                    }
                    direction="top"
                    eventDelay={0}
                    id={shift.shift + "ToolTip"}
                    padding={8}
                  />
                )
              ) : (
                <RegisterButton
                  onClick={() => requestRegister(shift.shift_id, i, shift.role)}
                >
                  Register
                </RegisterButton>
              )
            ) : null}
          </>
        ),
      };
    });

  const leftAlignment: "left" | "center" | "right" = "left";
  const centerAlignment: "left" | "center" | "right" = "center";
  const rightAlignment: "left" | "center" | "right" = "right";

  const tableColumns = [
    {
      id: "shift",
      name: "SHIFT",
      align: leftAlignment,
    },
    {
      id: "date",
      name: "DATE",
      align: leftAlignment,
    },
    {
      id: "time_start",
      name: "TIME START",
      align: rightAlignment,
    },
    {
      id: "time_end",
      name: "TIME END",
      align: rightAlignment,
    },
    {
      id: "spots_left",
      name: "SPOTS LEFT",
      align: centerAlignment,
    },
    {
      id: "roles",
      name: "ROLES",
      align: leftAlignment,
    },
    {
      id: "view",
      name: "View",
      hideLabel: true,
      align: leftAlignment,
    },
  ];

  return (
    <>
      {selectedShift && (
        <VolunteerShiftModal
          shiftId={selectedShift}
          open={viewModal}
          setOpen={setViewModal}
          isRegistered={isRegistered[selectedShiftIndex]}
          isDisableRegistered={isDisableRegistered[selectedShiftIndex]}
          onRegister={() => runOnSelectedShift(requestRegister)}
          onUnregister={() => runOnSelectedShift(handleUnregister)}
          signupCount={adminShifts[selectedShiftIndex]?.signup_count}
        />
      )}
      <SignupConfirmModal
        open={pendingSignup !== null && pendingConfirmShift !== null}
        shift={pendingConfirmShift}
        projected={pendingProjected || undefined}
        neighborsLosingBuffer={neighborsLosingBuffer}
        onCancel={() => setPendingSignup(null)}
        onConfirm={() => {
          if (!pendingSignup) return;
          const { shiftId, index, name } = pendingSignup;
          setPendingSignup(null);
          handleRegister(shiftId, index, name);
        }}
      />
      <Header
        title="Shift Schedule"
        subtitle="Register for your volunteer shifts"
      />
      <PageWrapper>
        <Flex column style={{ gap: "20px", paddingBottom: "16px" }}>
          <Text mods="heading h2">Shift Instructions</Text>
          <Subtitle>
            You must sign up for <strong>AT LEAST</strong>{" "}
            {minSignUps + DEFAULT_MIN_SHIFTS} shifts ({minSignUps} primary roles
            and {DEFAULT_MIN_SHIFTS}+ floor/security/supplementary), however you
            can take as many shifts as you wish!
          </Subtitle>
          <Flex column style={{ gap: "10px" }}>
            <RequirementRow>
              <RequirementIcon>
                {hasReadHandbook ? (
                  <Icon size="18px" name="perk-completed" />
                ) : (
                  <Icon size="18px" name="perk-not-started" />
                )}
              </RequirementIcon>
              <RequirementText>
                Read the{" "}
                <StyledLink href={VolunteerRoute.CONTACT}>
                  volunteer handbook
                </StyledLink>
              </RequirementText>
            </RequirementRow>
            <RequirementRow>
              <RequirementIcon>
                {primaryRolesCount >= minSignUps ? (
                  <Icon size="18px" name="perk-completed" />
                ) : primaryRolesCount > 0 ? (
                  <Icon size="18px" name="perk-in-progress" />
                ) : (
                  <Icon size="18px" name="perk-not-started" />
                )}
              </RequirementIcon>
              <RequirementText>
                Sign up for {minSignUps} <strong>{primaryRole}</strong>{" "}
                {minSignUps === 1 ? "shift" : "shifts"}
              </RequirementText>
            </RequirementRow>
            <RequirementRow>
              <RequirementIcon>
                {additionalRolesCount >= DEFAULT_MIN_SHIFTS ? (
                  <Icon size="18px" name="perk-completed" />
                ) : additionalRolesCount > 0 ? (
                  <Icon size="18px" name="perk-in-progress" />
                ) : (
                  <Icon size="18px" name="perk-not-started" />
                )}
              </RequirementIcon>
              <RequirementText>
                Sign up for two additional Floor Patrol, Security or
                Supplementary shift
              </RequirementText>
            </RequirementRow>
            <RequirementRow>
              <RequirementIcon>
                {overNightShiftCount > 0 ? (
                  <Icon size="18px" name="perk-completed" />
                ) : (
                  <Icon size="18px" name="perk-not-started" />
                )}
              </RequirementIcon>
              <RequirementText>
                Sign up for at least one overnight shift (start or end between
                12:00am–7:00am)
              </RequirementText>
            </RequirementRow>
          </Flex>
        </Flex>
        <ExportIcsButton
          type="button"
          disabled={myBufferShifts.length === 0}
          onClick={handleExportIcs}
        >
          <ExportDownloadIcon />
          Export .ics
        </ExportIcsButton>
        <HeaderFlex>
          <Text mods="heading h3">Schedule</Text>
          <ScheduleToggle
            currentView={currentView}
            onToggle={() => toggleView()}
          ></ScheduleToggle>
        </HeaderFlex>
        <div className="text-text-tertiary mb-4">
          All times are in Eastern Time (EDT)
        </div>
        <div style={{ width: "100%" }}>
          {currentView === SCHEDULE_VIEW_TYPE.LIST_VIEW && (
            <ShiftSearchBar
              value={search}
              onChange={(value) => {
                setPage(0);
                setSearch(value);
              }}
            />
          )}
          <ScheduleFilterBar
            shiftFilters={shiftFilters}
            setShiftFilters={setShiftFilters}
            onFiltersChange={() => setPage(0)}
          />

          <>
            {fetchingShifts ? (
              <Spinner text="Loading shifts..." />
            ) : currentView === SCHEDULE_VIEW_TYPE.CALENDAR_VIEW ? (
              <>
                <VolunteerShiftCalendarView
                  fetchingShifts={fetchingShifts}
                  volunteerShifts={adminShifts}
                  shiftFilters={shiftFilters}
                  isRegistered={isRegistered}
                  isDisableRegistered={isDisableRegistered}
                  onlyMyShifts={onlyMyShifts}
                  toggleOnlyMyShifts={() => {
                    setOnlyAvailableShifts(false);
                    setOnlyMyShifts((prev) => !prev);
                  }}
                  onlyAvailableShifts={onlyAvailableShifts}
                  toggleOnlyAvailableShifts={() => {
                    setOnlyMyShifts(false);
                    setOnlyAvailableShifts((prev) => !prev);
                  }}
                  setSelectedShift={setSelectedShift}
                  setViewModal={setViewModal}
                />

                <ShiftLegend />
              </>
            ) : (
              <>
                <Table
                  columns={tableColumns}
                  data={tableData}
                  horizontalPadding={16}
                  verticalPadding={10}
                  showFooter
                  page={page}
                  itemsPerPage={itemsPerPage}
                />
                <PageBar
                  page={page}
                  itemsPerPage={itemsPerPage}
                  totalItems={tableData.length}
                  onPageChange={setPage}
                  onItemsPerPageChange={setItemsPerPage}
                />
              </>
            )}
          </>
        </div>
      </PageWrapper>
    </>
  );
};

const RolesContainer = styled.div`
  border-radius: 27.568px;
  padding: 4px 9.189px;
  background: #e5e7eb;
  text-align: center;
  font-weight: 700;
  font-size: 11.748px;
  color: #6b7280;
`;

const ExportIcsButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 0;
  margin-bottom: 36px;
  padding: 6px 10px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 4px;
  background: ${({ theme }) => theme.globalConstants.color.white};
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.globalConstants.color.backgroundGray};
  }
`;

const ExportDownloadIcon = styled(FileDownloadIcon)`
  width: 20px;
  height: 20px;
  flex: none;
  fill: #000;
`;

const TooltipText = styled.p`
  font-size: 12px;
  color: white;
  font-weight: 700;
`;

export const RegisterButton = styled.button`
  border-radius: 4px;
  border: 1px solid #2395c6;
  background: #43afde;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
  padding: 4px 10px;

  color: #fff;
  font-size: 16px;
  font-weight: 700;
  text-align: center;
`;

export const DisabledRegisterButton = styled.button`
  padding: 4px 10px;
  border-radius: 4px;
  border: 1px solid #88cdea;
  background: #abdbf0;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);

  color: #fff;
  font-size: 16px;
  font-weight: 700;
  text-align: center;

  &:hover {
    cursor: not-allowed;
  }
`;

export const UnregisterButton = styled.button`
  padding: 4px 10px;
  border-radius: 4px;
  border: 1px solid #d1d5db;
  background: #fff;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);

  color: #d95858;
  font-size: 16px;
  font-weight: 700;
  text-align: center;
`;

const Subtitle = styled.p`
  color: #6b7280;
  font-size: 16px;
`;

const RequirementRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const RequirementIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 2px;
`;

const RequirementText = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 16px;
  min-width: 0;
  flex: 1;
`;

const StyledLink = styled(Link)`
  font-size: 16px;
  text-decoration: none !important;
`;

const HeaderFlex = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  flex-direction: row;
  justify-content: space-between;
`;
